import { DownOutlined, InfoCircleOutlined, VideoCameraFilled, PlusOutlined, UpOutlined } from "@ant-design/icons";
import { MdDeleteForever } from "react-icons/md";

import {
  Row,
  Col,
  Button,
  message,
  Form,
  Input,
  Select,
  Card,
  Grid,
  InputNumber,
  Switch,
  Tooltip,
  Checkbox,
  Alert,
} from "antd";
import API, { apis } from "common/apis";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getCities,
  getClusterListing,
  getDarkStores,
  getPpdSlots,
  getSocieties,
  submitSocietyAmendment,
  cancelSocietyAmendment,
} from "store/admin-slice/adminServicesSlice";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import DndItem from "./component/DnDItem";
import { ClusterInfo } from "common/interface/Society";
import { MapPolygonViewer } from "components/MapPolygonViewer";
import { wktPolygonValidator } from "common/utils";
import { validateCoordinates } from "common/geoUtils";
import { buildSocietyAmendmentPayload } from "common/societyAmendmentUtils";
import { routes } from "common/routes";
import { Link } from "react-router-dom";

const { useBreakpoint } = Grid;

const MESSAGE_KEY = "CREATE_SOCIETY";
export const CreateNewSocietyPage = () => {
  useDocumentTitle("Create New Society");
  const { status, error, darkStores, societies, clusters, ppdSlots, cities, amendmentStatus, amendmentError } =
    useAppSelector((state) => state.adminServices);
  const [towers, setTowers] = useState<String[]>([]);
  const [clustersList, setClustersList] = useState<ClusterInfo[]>([]);
  const [formWidth, setFormWidth] = useState("100%");
  const [form] = Form.useForm();
  const [filteredStores, setFilteredStores] = useState<typeof darkStores>([]);
  const [pendingAmendmentId, setPendingAmendmentId] = useState<number | null>(null);
  const [isFormLocked, setIsFormLocked] = useState(false);
  // const [filteredRightAwayStores, setFilteredRightAwayStores] = useState(rightAwayStores);
  const [nameSuggestions, setNameSuggestions] = useState<{ id: number; name: string; distanceInMeters: number; isVerified: boolean }[]>([]);
  const nameDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const screens = useBreakpoint();
  const sampleAreaPolygon = "POLYGON ((77.058178 28.443964, 77.066364 28.452456, 77.069405 28.450173, 77.065957 28.446555, 77.065055 28.447231, 77.060305 28.44224, 77.063182 28.439989, 77.060367 28.437085, 77.057915 28.438874, 77.059033 28.439945, 77.058316 28.440443, 77.060117 28.442335, 77.058178 28.443964))";
  const areaPolygonValue = Form.useWatch('areaPolygon', form);
  const latitudeValue = Form.useWatch('latitude', form);
  const longitudeValue = Form.useWatch('longitude', form);
  const selectedCityId = Form.useWatch('cityId', form);

  // Map visibility logic - show map when either condition is met:
  // 1. Area polygon is provided
  // 2. Both latitude and longitude are provided
  const hasValidCoordinates = latitudeValue && longitudeValue && 
    validateCoordinates(latitudeValue, longitudeValue);
  const hasAreaPolygon = areaPolygonValue && areaPolygonValue.trim();
  const shouldShowMap = hasValidCoordinates || hasAreaPolygon;

  useEffect(() => {
    if (id != null) {
      let societyData = societies.data.find((e) => e.id == id);
      if (societyData) {
        setTowers(societyData.tower.map((e) => e.towerName));
        setClustersList(
          clusters,
        );
        form.setFieldsValue({
          ...societyData,
          houseHolds: societyData?.metadata?.houseHolds,
          isPrepaid: societyData?.metadata?.isPrepaid,
          validSlots: societyData?.metadata?.validSlots ?? [],
          active: societyData.active,
          dockNumber: societyData?.metadata?.dockNumber,
          verified: societyData.verified ?? 0
        });
        if (societyData.pendingAmendmentId) {
          setPendingAmendmentId(societyData.pendingAmendmentId);
          setIsFormLocked(true);
        }
      } else if (societies.data.length === 0) {
        // If societies data is not loaded yet, load it
        dispatch(getSocieties({ pageNo: 1, pageSize: 25, filter: "" }));
      } else {
        navigate("/admin-services/society");
      }
    }
  }, [societies, id, dispatch, navigate]);

  useEffect(() => {
    if (amendmentStatus === "saving") {
      message.loading({ content: "Submitting request", key: MESSAGE_KEY });
    } else if (amendmentStatus === "error" && amendmentError) {
      if (amendmentError.toLowerCase().includes("already pending")) {
        setIsFormLocked(true);
      }
      message.error({ content: amendmentError, key: MESSAGE_KEY });
    }

    return () => {
      message.destroy();
    };
  }, [amendmentStatus, amendmentError]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Loading", key: MESSAGE_KEY });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    }
  }, [status, error]);

  useEffect(() => {
    dispatch(getDarkStores());
    // dispatch(getRightAwayStores());
    dispatch(getPpdSlots());
    dispatch(getCities());

    dispatch(getClusterListing());
    return () => {
      message.destroy();
    };
  }, []);

  // Update filteredStores whenever darkStores or selectedCityId changes
  useEffect(() => {
    if (selectedCityId) {
      // Filter stores by selected city
      const cityFilteredStores = darkStores.filter(store => store.cityId === selectedCityId);
      setFilteredStores(cityFilteredStores);
    } else {
      // If no city is selected, show all stores (or empty if no stores loaded yet)
      setFilteredStores(darkStores);
    }
  }, [darkStores, selectedCityId]);

  const onSave = async () => {
    if (isFormLocked) return;
    try {
      await form.validateFields();
      const formData = form.getFieldsValue();
      const payload = buildSocietyAmendmentPayload(formData, cities, id);

      if (id != null) {
        payload.active = form.getFieldValue("active");
        const result = await dispatch(submitSocietyAmendment({
          entityType: "SOCIETY",
          entityId: id,
          requestType: "UPDATE",
          payload,
        }));
        if (submitSocietyAmendment.fulfilled.match(result)) {
          message.success({ content: "Society change request submitted for approval!", key: MESSAGE_KEY });
          navigate("/admin-services/society");
        }
      } else {
        const result = await dispatch(submitSocietyAmendment({
          entityType: "SOCIETY",
          requestType: "CREATE",
          payload,
        }));
        if (submitSocietyAmendment.fulfilled.match(result)) {
          message.success({ content: "Society create request submitted for approval!", key: MESSAGE_KEY });
          form.resetFields();
          navigate(`/${routes.CLUSTER_SOCIETY.SOCIETY_AMENDMENTS}`);
        }
      }
    } catch (e) {
      console.log(e);
    }
  };

  const onCancelPendingRequest = async () => {
    if (!pendingAmendmentId) return;
    const result = await dispatch(cancelSocietyAmendment({ id: pendingAmendmentId }));
    if (cancelSocietyAmendment.fulfilled.match(result)) {
      message.success({ content: "Request cancelled successfully", key: MESSAGE_KEY });
      setPendingAmendmentId(null);
      setIsFormLocked(false);
      if (id) {
        dispatch(getSocieties({ pageNo: 1, pageSize: 25, filter: "" }));
      }
    }
  };

  const onCancelSave = () => {
    navigate(-1);
  };

  const handleFormValuesChange = (changedValues: any) => {
    const fieldName = Object.keys(changedValues)[0];

    if (fieldName === "cityId") {
      // Clear existing store and cluster selections when city changes
      form.setFieldsValue({
        storeId: undefined,
        clusterId: undefined,
      });
    }

    if (fieldName === "storeId") {
      // Clear cluster selection and filter clusters based on selected store
      form.setFieldsValue({ clusterId: undefined });
      const selectedStoreId = changedValues[fieldName];
      if (selectedStoreId) {
        // Filter clusters based on selected store ID
        const updatedFilteredClusters = clusters.filter(cluster => cluster.storeId === selectedStoreId);
        setClustersList(updatedFilteredClusters);
      } else {
        // If no store selected, show all clusters
        setClustersList(clusters);
      }
    }
  };

  useEffect(() => {
    if (screens.lg) {
      setFormWidth("70%");
    } else {
      setFormWidth("100%");
    }
  }, [screens]);

  const _validateStores = (_: any, value: any) => {
    const storeIds = form.getFieldValue('storeIds') || [];
    const rightAwayStoreIds = form.getFieldValue('rightAwayStoreIds') || [];

    if (storeIds.length === 0 && rightAwayStoreIds.length === 0) {
      return Promise.reject('At least one store type must be selected');
    }
    return Promise.resolve();
  };

  const handleNameSearch = (value: string) => {
    if (nameDebounceTimer.current) clearTimeout(nameDebounceTimer.current);
    if (value.length < 3) {
      setNameSuggestions([]);
      return;
    }
    nameDebounceTimer.current = setTimeout(async () => {
      try {
        const response = await API.get(apis.getNearbySocieties, {
          params: { latitude: latitudeValue, longitude: longitudeValue, name: value },
        });
        const raw = response.data;
        const results = Array.isArray(raw) ? raw : (raw?.societies ?? []);
        setNameSuggestions(results);
      } catch {
        setNameSuggestions([]);
      }
    }, 300);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(sampleAreaPolygon).then(() => {
      message.success({
        content: 'Sample text copied to clipboard!',
        key: MESSAGE_KEY,
        duration: 1.5,
      });
    }).catch((err) => {
      message.error('Failed to copy text: ', err);
    });
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Row>
        <Col xs={24}>
          <AppPageHeader
            title={id != null ? "Update Society" : "Create New Society"}
          />
        </Col>
      </Row>

      {isFormLocked && pendingAmendmentId && (
        <Row style={{ marginBottom: "1rem" }}>
          <Col xs={24}>
            <Alert
              type="warning"
              showIcon
              message={`Pending approval (#${pendingAmendmentId})`}
              description={
                <>
                  This society has a pending change request. Editing is disabled until the request is resolved.{" "}
                  <Link to={`/${routes.CLUSTER_SOCIETY.SOCIETY_AMENDMENT_DETAIL.replace(":id", String(pendingAmendmentId))}`}>
                    View request
                  </Link>
                </>
              }
              action={
                <Button size="small" danger onClick={onCancelPendingRequest} loading={amendmentStatus === "saving"}>
                  Cancel Request
                </Button>
              }
            />
          </Col>
        </Row>
      )}
      
      {/* Main content row with form on left and map on right */}
      <Row gutter={[16, 16]}>
        {/* Form Section - Left Side */}
        <Col xs={24} lg={shouldShowMap ? 14 : 24}>
          <Form
            form={form}
            name="vendor-filter-form"
            layout="vertical"
            style={{ width: "100%" }}
            onValuesChange={handleFormValuesChange}
            disabled={isFormLocked}
          >
        {/* Lat / Long — must be filled before other fields are enabled */}
        <Col xs={24}>
          <Row>
            <Col xs={24} md={8}>
              <Form.Item
                name="latitude"
                label="Latitude"
                validateTrigger="onBlur"
                rules={[
                  { required: true },
                  {
                    pattern: new RegExp(/^-?\d+(\.\d+)?$/),
                    message: 'Please enter a valid number format',
                  },
                  {
                    validator: (_, value) => {
                      if (value === '' || value === null || value === undefined) return Promise.resolve();
                      const lat = parseFloat(value);
                      if (lat < -90 || lat > 90) return Promise.reject('Latitude out of expected boundary range');
                      if (lat < 8.0 || lat > 37.1) return Promise.reject('Latitude out of expected boundary range');
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="Latitude" size="small" type="number" allowClear required />
              </Form.Item>
            </Col>
            <Col xs={24} md={3}></Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="longitude"
                label="Longitude"
                validateTrigger="onBlur"
                rules={[
                  { required: true },
                  {
                    pattern: new RegExp(/^-?\d+(\.\d+)?$/),
                    message: 'Please enter a valid number format',
                  },
                  {
                    validator: (_, value) => {
                      if (value === '' || value === null || value === undefined) return Promise.resolve();
                      const lng = parseFloat(value);
                      if (lng < -180 || lng > 180) return Promise.reject('Longitude out of expected boundary range');
                      if (lng < 68.0 || lng > 97.5) return Promise.reject('Longitude out of expected boundary range');
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="Longitude" size="small" type="number" allowClear required />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col xs={24}>
          <Row>
            <Col xs={24} md={8}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true }]}
              >
                <Input
                  placeholder="Name"
                  size="small"
                  allowClear
                  disabled={!hasValidCoordinates}
                  onChange={(e) => handleNameSearch(e.target.value)}
                />
              </Form.Item>
              {nameSuggestions.length > 0 && (
                <div style={{
                  background: '#fffbe6',
                  border: '1px solid #ffe58f',
                  borderRadius: 4,
                  padding: '8px 12px',
                  marginTop: -8,
                  marginBottom: 8,
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 6, color: '#ad6800' }}>
                    ⚠ Similar societies already exist ({nameSuggestions.length})
                  </div>
                  <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                    {nameSuggestions.map((s) => (
                      <div key={s.id} style={{
                        padding: '4px 0',
                        borderBottom: '1px solid #ffe58f',
                        fontSize: 12,
                        color: '#333',
                      }}>
                        <span style={{ fontWeight: 500 }}>{s.name}</span>
                        <span style={{ marginLeft: 8, color: '#888' }}>ID: {s.id}</span>
                        <span style={{ marginLeft: 8, color: '#888' }}>{s.distanceInMeters}m away</span>
                        <span style={{ marginLeft: 8, color: s.isVerified ? '#52c41a' : '#faad14' }}>
                          {s.isVerified ? '✓ Verified' : '○ Unverified'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Col>

            <Col xs={24} md={2}></Col>

            <Col xs={24} md={5}>
              <Form.Item name={["metadata", "houseHolds"]} label="House Holds">
                <Input placeholder="House Holds" size="small" type="number" disabled={!hasValidCoordinates} />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col xs={24}>
          <Row>
            <Col xs={24} md={14}>
              <Form.Item name={["metadata", "validSlots"]} label="Valid Slots">
                <Select
                  mode="multiple"
                  placeholder="Select valid slots"
                  style={{ width: "100%" }}
                  defaultValue={[]}
                  disabled={!hasValidCoordinates}
                >
                  {ppdSlots.map((ppdSlot, i) => (
                    <Select.Option key={ppdSlot.id} value={ppdSlot.id}>
                      {ppdSlot.slot}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={2}></Col>

            <Col xs={24} md={8}>
              <Form.Item
                name={["metadata", "isPrepaid"]}
                label="Is Prepaid"
                valuePropName="checked"
              >
                <Switch disabled={!hasValidCoordinates} />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col xs={24}>
          <Row>
            <Col xs={24} md={8}>
              <Form.Item
                name={["isContactDelivery"]}
                label="Is Contact Delivery"
                rules={[{ required: true }]}
              >
                <Select placeholder="Select contact delivery status" disabled={!hasValidCoordinates}>
                  <Select.Option value={0}>No</Select.Option>
                  <Select.Option value={1}>Yes</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col xs={24}>
          <Row>
            <Col xs={24} md={19}>
              <Form.Item
                name="areaPolygon"
                label="Area Polygon"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value) return wktPolygonValidator(value);
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input.TextArea
                  placeholder="Enter area polygon coordinates in WKT format"
                  size="small"
                  allowClear
                  rows={4}
                  disabled={!hasValidCoordinates}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={1} style={{ paddingTop: '30px', paddingLeft: '10px' }}>
              <Tooltip title={`Sample input: ${sampleAreaPolygon}`}>
                <InfoCircleOutlined style={{ fontSize: '16px', color: '#1890ff' }} onClick={copyToClipboard} />
              </Tooltip>
              <VideoCameraFilled style={{ fontSize: '16px', color: 'red' }} onClick={() => window.open('https://youtu.be/Eermg5gJ5ns', '_blank')} />
            </Col>
          </Row>
        </Col>

        <Col xs={24}>
          <Row>
            <Col md={6} xs={24}>
              {cities && <Form.Item
                name="cityId"
                label="City"
                rules={[{ required: true }]}
              >
                <Select
                  placeholder="Select City"
                  style={{ width: "100%" }}
                  disabled={!hasValidCoordinates}
                >
                  {cities.map((city) => (
                    <Select.Option key={city.id} value={city.id} onChange={() => {
                      form.setFieldsValue({ "city": city.name, "state": city.state });
                    }}>
                      {city.name}, {city.state}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>}
            </Col>
            <Form.Item name="city" hidden><Input /></Form.Item>
            <Form.Item name="state" hidden><Input /></Form.Item>

            <Col xs={24} md={1}></Col>

            <Col md={5} xs={24}>
              <Form.Item
                name="pincode"
                label="Pincode"
                rules={[{ required: true }]}
              >
                <Input
                  placeholder="Pincode"
                  size="small"
                  maxLength={6}
                  allowClear
                  type="number"
                  required
                  disabled={!hasValidCoordinates}
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col xs={24}>
          <Row>
            <Col xs={24} md={6}>
              <Form.Item name="storeId" label="Store" rules={[]}>
                <Select
                  allowClear
                  showSearch
                  size="small"
                  disabled={!hasValidCoordinates}
                  filterOption={(input, option) => {
                    return (
                      option?.children
                        ?.toString()
                        .toLowerCase()
                        .includes(input?.toLowerCase() ?? "") ?? false
                    );
                  }}
                >
                  {filteredStores.map((status) => (
                    <Select.Option value={status.id} key={status.id}>
                      {status.name} || {status.id}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col xs={24}>
          <Row>
            <Col xs={24} md={6}>
              <Form.Item name="clusterId" label="Cluster" rules={[{ required: true }]}>
                <Select
                  showSearch
                  size="small"
                  disabled={!hasValidCoordinates}
                  filterOption={(input, option) => {
                    return (
                      option?.children
                        ?.toString()
                        .toLowerCase()
                        .includes(input?.toLowerCase() ?? "") ?? false
                    );
                  }}
                >
                  {clustersList.map((status) => (
                    <Select.Option value={status.id} key={status.id}>
                      {status.name} || {status.id}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={1}></Col>
            <Col xs={24} md={6}>
              <Form.Item name="deliverySeq" label="Delivery Seq" rules={[{ required: true }]}>
                <InputNumber
                  placeholder="Delivery Sequence"
                  size="small"
                  required
                  disabled={!hasValidCoordinates}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name={["metadata", "dockNumber"]} label="Dock Number" rules={[{ required: true }]}>
                <Input placeholder="Dock Number" size="small" type="number" disabled={!hasValidCoordinates} />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col xs={24} md={12}>
          <Card
            title={`Towers List`}
            key={`towers list`}
            bodyStyle={{ padding: "1.5rem 1.5rem 0 1rem" }}
          >
            <>{"Tower Name"}</>
            <Form.Item
              name="checkTowers"
            >
              <Form.List
                name="tower"
              >
                {(fields, { add, remove, move }) => (
                  <>
                    {fields.map(({ key, name, ...restField }, index) => (
                      <DndItem
                        key={index}
                        index={index}
                        moveItem={(a: any, b: any) => {
                          move(a, b);
                          let temp = form.getFieldValue("tower");
                          temp[a]["deliverySequence"] = a + 1;
                          temp[b]["deliverySequence"] = b + 1;
                          form.setFieldsValue({ tower: temp });
                        }}
                        children={
                          <Card
                            style={{ margin: "0 0 0 0" }}
                            bodyStyle={{ padding: "1rem 1rem 0 .5rem" }}
                          >
                            <Col key={index}>
                              <Row align="middle" justify="center">
                                <Col style={{ width: "10%" }}>
                                  {index + 1}
                                  {"."}
                                </Col>
                                <Col style={{ width: "90%" }}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, "towerName"]}
                                    rules={[
                                      {
                                        required: true,
                                        message: "Name Required",
                                      },
                                    ]}
                                  >
                                    <Input
                                      placeholder="Name"
                                      required
                                      suffix={
                                        <MdDeleteForever
                                          style={{ cursor: "pointer" }}
                                          size={22}
                                          color="#cc0000"
                                          onClick={() => remove(name)}
                                        />
                                      }
                                    />
                                  </Form.Item>
                                </Col>
                                {
                                  <Col style={{ width: "20%" }}>
                                    <Button.Group>
                                      <Button
                                        size="small"
                                        icon={<DownOutlined />}
                                        onClick={() => move(index, index + 1)}
                                        disabled={index === fields.length - 1}
                                      />
                                      <Button
                                        size="small"
                                        icon={<UpOutlined />}
                                        onClick={() => move(index, index - 1)}
                                        disabled={index == 0}
                                      />
                                    </Button.Group>
                                  </Col>
                                }
                              </Row>

                              <Form.Item
                                {...restField}
                                name={[name, "deliverySequence"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Sequence Required",
                                  },
                                ]}
                                noStyle
                              />
                            </Col>
                          </Card>
                        }
                      />
                    ))}
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() =>
                          add({
                            deliverySequence:
                              (form.getFieldValue("tower") ?? []).length + 1,
                          })
                        }
                        block
                        icon={<PlusOutlined />}
                      >
                        Add Tower
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Form.Item>
          </Card>
        </Col>

        {id != null && (
          <Col xs={24} style={{ marginTop: "2rem" }}>
            <Row>
              <Col xs={24} md={8}>
                <Form.Item
                  name="verified"
                  label="Mark as Verified"
                  valuePropName="checked"
                >
                  <Checkbox>
                    Verified
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>
          </Col>
        )}
          </Form>
        </Col>
        
        {/* Map Section - Right Side */}
        {shouldShowMap && (
          <Col xs={24} lg={10}>
            <div style={{ 
              position: 'sticky', 
              top: '20px',
              paddingLeft: screens.lg ? '8px' : '0'
            }}>
              <MapPolygonViewer 
                wktData={areaPolygonValue || ''}
                markerPosition={
                  hasValidCoordinates 
                    ? [latitudeValue, longitudeValue]
                    : undefined
                }
                markerPopup={
                  hasValidCoordinates 
                    ? `Society Location\nLat: ${latitudeValue}, Lng: ${longitudeValue}`
                    : undefined
                }
                height={screens.lg ? 500 : 400}
                showControls={true}
                showPolygonInfo={true}
              />
            </div>
          </Col>
        )}
      </Row>
      
      {/* Action Buttons */}
      <Row style={{ marginTop: "1rem" }}>
        <Col>
          <Button
            type="primary"
            onClick={onSave}
            loading={amendmentStatus === "saving"}
            disabled={isFormLocked || amendmentStatus === "saving"}
          >
            {id != null ? "Submit Update Request" : "Submit Create Request"}
          </Button>
        </Col>
        <Col
          style={{
            justifyContent: "left",
            marginLeft: "20px",
          }}
        >
          <Button onClick={onCancelSave} disabled={amendmentStatus === "saving"}>
            Cancel
          </Button>
        </Col>
      </Row>
    </DndProvider>
  );
};